import { BlobServiceClient } from '@azure/storage-blob';
import { useEffect, useState } from 'react';
import { useMsalTokenProvider } from './useMsalTokenProvider';

/**
 * A React hook to create and manage an instance of Azure BlobServiceClient using Azure AD token.
 *
 * This hook uses the MSAL token acquired for Azure Blob Storage to authenticate and
 * create an instance of BlobServiceClient. It ensures that the BlobServiceClient is
 * updated whenever a new token is acquired.
 *
 * @param {string} storageAccount - The name of the Azure Storage Account.
 * @returns {BlobServiceClient | null} An instance of BlobServiceClient authenticated
 *          with the Azure AD token or `null` if the token is not available.
 *
 */
const useBlobServiceClient = (storageAccount: string) => {
    const { tokenProvider } = useMsalTokenProvider({
        scopes: ['https://storage.azure.com/.default'],
    });
    const [blobServiceClient, setBlobServiceClient] =
        useState<BlobServiceClient | null>(null);

    useEffect(() => {
        const getClient = async () => {
            if (tokenProvider) {
                const client = new BlobServiceClient(
                    `https://${storageAccount}.blob.core.windows.net`,
                    tokenProvider
                );
                setBlobServiceClient(client);
            }
        };
        getClient();
    }, [tokenProvider, storageAccount]);

    return blobServiceClient;
};

export default useBlobServiceClient;
